import { AttachmentType, QuestionAttachment, SkillType } from '@types';
import { formatISODate } from '@utils/date';
import { ImageViewer } from '@components';

import { cardThemes } from './config';

/* eslint-disable no-plusplus */
export const getCurrentSection = (sections: any[]) => {
  for (let i = 0; i < sections?.length; i++) {
    const { questions } = sections[i];
    for (let j = 0; j < questions?.length; j++) {
      if (questions[j].status === 'NOT_STARTED') {
        return i;
      }
    }
  }
  return -1;
};

export const getQuestionNumber = (selectedQuestion: any, questionList: any) =>
  questionList.findIndex(
    (quesion: any) => quesion?.questionId === selectedQuestion?.questionId
  ) + 1;

export const getHomeworkCardTheme = (id?: string) => {
  if (id === undefined) {
    return cardThemes[0];
  }

  const index = Number(id) % cardThemes.length;

  return cardThemes[index];
};

const questionTypePerformanceTypesOrder = [
  'MCQ',
  'Short Answer',
  'Long Answer',
  'Fill In The Blanks',
  'MCQ Multi Select',
  'Audio'
];

export const getQuestionTypePerformanceValue = (
  questionTypePerformance: any
) => {
  const percentages = questionTypePerformanceTypesOrder.map((type) => {
    const item = questionTypePerformance?.find((q: any) => q.type === type);

    if (!item) return 0;

    if (Number(item.totalPoints) === 0) {
      return 0;
    }

    return Number(
      ((Number(item.pointsSum) / Number(item.totalPoints)) * 100).toFixed(1)
    );
  });

  return percentages;
};

export const getCommonErrortypeValue = (commonErrortypes: any) => {
  const percentages = commonErrortypes?.map(
    (error: any) => error?.percentageValue || 0
  );
  return percentages;
};

export const getCommonErrorTypeLabel = (commonErrortypes: any) => {
  const labels = commonErrortypes?.map((error: any) => error?.type);
  return labels;
};

export const getCompletionTimeColor = (time: number) => {
  if (time < 5) {
    return '#B2DDA3';
  }
  if (time <= 10 && time >= 5) {
    return '#95CDFE';
  }
  if (time > 10 && time <= 15) {
    return '#FFDCAA';
  }
  return '#FEBEB5';
};

export const getCompletionTimeData = (data: number[]) =>
  data?.map((item, index) => ({
    id: index + 1,
    time: item,
    bgColor: getCompletionTimeColor(item)
  }));

export const getSkillLabels = (skills: SkillType[]) =>
  skills?.map((skill: SkillType) => skill?.skillName);

export const getSkillValues = (skills: SkillType[]) =>
  skills?.map((skill: SkillType) => Number(skill?.successRate));

export const getScoreTrendLabel = (scoreTrends: any[]) =>
  scoreTrends?.map(
    (scoreTrend: any) =>
      `${scoreTrend?.homeworkName};${formatISODate({
        date: scoreTrend?.assignmentToDate
      })}`
  );

export const getStudentsMarkPercentage = (ScoreTrend: any[]) =>
  ScoreTrend?.map((scoreTrend: any) => Number(scoreTrend?.point));

export const getClassMarkPercentage = (ScoreTrend: any[]) =>
  ScoreTrend?.map((scoreTrend: any) =>
    Number(scoreTrend?.classPointPercentage)
  );

const completionRateOrder = [
  'COMPLETED',
  'PARTIALLY_COMPLETED',
  'NOT_COMPLETED'
];

export const getCompletionRateOrderValue = (completiontypes: any) => {
  const percentages = completionRateOrder.map((type) => {
    const item = completiontypes?.find((q: any) => q.status === type);
    if (!item) return 0;
    return item?.percentage;
  });
  return percentages;
};

export const getPerformanceColor = (value: number) => {
  let textColor = 'text-red-500';
  if (value >= 80) {
    textColor = 'text-green';
  } else if (value >= 50) {
    textColor = 'text-warning';
  }
  return textColor;
};

export const getRemainingDays = (date: string) => {
  const currentDate = new Date();
  currentDate.setHours(0, 0, 0, 0);

  const givenDate = new Date(date);
  givenDate.setHours(0, 0, 0, 0);

  const diffTime = givenDate.getTime() - currentDate.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

export const hasRequiredKeys = (
  obj: Record<string, any>,
  keys: string[]
): boolean => keys?.every((key) => key in obj);

export const showAttachment = (
  attachment: QuestionAttachment,
  isMagnifierRequired?: boolean
) => {
  switch (attachment.type) {
    case AttachmentType.IMAGE:
      return (
        <ImageViewer
          src={attachment?.attachments.url}
          alt={attachment?.attachments.name}
          className="m-1 cursor-pointer"
          key={attachment?.attachments?.name}
          isExpandable
          isMagnifierRequired={isMagnifierRequired}
        />
      );
    case AttachmentType.AUDIO:
      // eslint-disable-next-line jsx-a11y/media-has-caption
      return <audio src={attachment?.attachments.url} controls />;
    default:
      return <div />;
  }
};

export const questionProcessed = (questionDetails: any) =>
  questionDetails?.question?.replace(/{{blanks}}|{{blank}}/g, ' ____ ');

export const truncateLabel = (label: string, maxLength = 25) => {
  if (label.length <= maxLength) return label;
  return `${label.slice(0, maxLength)}...`;
};
